import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../lib/api.js";
import type { PageWidgetSource } from "../lib/pageWidgets.js";
import { widgetSourceKey } from "../lib/pageWidgets.js";
import { resolveEchartsOption, type PageFilterRenderModel as RenderModel } from "../lib/pageFilters.js";
import type { ReactiveParam } from "../lib/reactiveVisualization.js";
import { Chart } from "./Chart.js";
import { TanTableViz } from "./TanTableViz.js";
import { AiContentRenderer } from "./AiContentRenderer.js";

export type PageBindableTarget = {
  node_id: string;
  // ADR-0056: node_type includes "reactive_visualization" alongside the legacy
  // "output.visualization" / "ai_provider" values. Left as `string` to match
  // the hub's loose typing and avoid cascading narrowing through the widget
  // source flow.
  node_type: string;
  name: string;
  // ADR-0056: port_id now includes "render_out" (reactive_visualization's
  // compiled chart) alongside "tabular_out" / "content_out" / "refs_out" /
  // "prompt_out".
  port_id: string;
  port_label: string;
  last_run_at: string | null;
  has_cache: boolean;
  row_selection?: string;
  /** ADR-0056: populated only when node_type === "reactive_visualization". */
  parameters?: ReactiveParam[];
};

export type PageBindableSourceRow = {
  operation_id: string;
  operation_name: string;
  operation_status: string;
  targets: PageBindableTarget[];
};

type ResolveRow = {
  source_key: string;
  name?: string;
  last?: { renderModel?: RenderModel; runAt?: string | null };
};

/**
 * ADR-0070 §3.6 — show a readable node-type label on each picker tile so users
 * can tell TanTable / KPI Card / Reactive Viz / AI Provider apart at a glance.
 * Falls back to the raw node_type string for unrecognized values (forward-
 * compatible with future node types).
 */
function nodeTypeLabel(nodeType: string): string {
  switch (nodeType) {
    case "tantable": return "TanTable";
    case "kpi_card": return "KPI Card";
    case "reactive_visualization": return "Reactive Viz";
    case "ai_provider": return "AI Provider";
    default: return nodeType;
  }
}

function targetToSource(opId: string, t: PageBindableTarget): PageWidgetSource {
  const port = t.port_id.trim() || "tabular_out";
  return {
    type: "operation_node",
    operation_id: opId,
    node_id: t.node_id,
    // ADR-0056: "render_out" is a valid widget-source port_id for
    // reactive_visualization nodes.
    ...(port !== "tabular_out" ? { port_id: port as "content_out" | "refs_out" | "prompt_out" | "render_out" } : {})
  };
}

function SourceTilePreview({
  portId,
  renderModel,
  hasCache
}: {
  portId: string;
  renderModel?: RenderModel;
  hasCache: boolean;
}) {
  const scale = 0.22;
  const boxStyle: React.CSSProperties = {
    width: "100%",
    aspectRatio: "8 / 5",
    overflow: "hidden",
    borderRadius: 8,
    background: "var(--bg-subtle, #f1f5f9)",
    border: "1px solid var(--border, #e2e8f0)",
    position: "relative"
  };
  const inner: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    transform: `scale(${scale})`,
    transformOrigin: "top left",
    width: `${100 / scale}%`,
    height: `${100 / scale}%`,
    pointerEvents: "none" as const
  };

  if (!hasCache || !renderModel) {
    return (
      <div className="pageSourceTilePreview pageSourceTilePreviewEmpty" style={{ ...boxStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div className="muted" style={{ padding: 12, fontSize: 12, lineHeight: 1.4, textAlign: "center" }}>
          No preview — run the operation first.
        </div>
      </div>
    );
  }

  if (portId === "content_out" && renderModel.kind === "ai_content") {
    return (
      <div style={boxStyle}>
        <div style={{ ...inner, overflow: "hidden" }}>
          <AiContentRenderer model={renderModel} />
        </div>
      </div>
    );
  }

  if (portId === "refs_out") {
    return (
      <div className="pageSourceTilePreview" style={{ ...boxStyle, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <span className="muted" style={{ fontSize: 13 }}>
          Citations
        </span>
      </div>
    );
  }

  if (portId === "prompt_out" && renderModel.kind === "ai_production_prompt") {
    const snippet = renderModel.text.trim().slice(0, 200);
    return (
      <div className="pageSourceTilePreview" style={{ ...boxStyle, padding: 8, overflow: "hidden" }}>
        <div className="muted" style={{ fontSize: 10, marginBottom: 4 }}>
          Production prompt
        </div>
        <pre
          style={{
            margin: 0,
            fontSize: 9,
            lineHeight: 1.35,
            fontFamily: "ui-monospace, monospace",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            maxHeight: 72,
            overflow: "hidden"
          }}
        >
          {snippet || "(empty)"}
        </pre>
      </div>
    );
  }

  if (renderModel.kind === "echarts") {
    return (
      <div style={boxStyle}>
        <div style={inner}>
          <Chart option={resolveEchartsOption(renderModel)} height={120} />
        </div>
      </div>
    );
  }

  if (renderModel.kind === "kpi") {
    return (
      <div style={boxStyle}>
        <div style={{ ...inner, padding: 8 }}>
          {/* KPI minimal */}
          <div style={{ fontSize: 11 }}>KPI</div>
        </div>
      </div>
    );
  }

  if (renderModel.kind === "tantable") {
    return (
      <div style={boxStyle}>
        <div style={inner}>
          <TanTableViz model={renderModel} />
        </div>
      </div>
    );
  }

  return (
    <div className="pageSourceTilePreview" style={boxStyle}>
      <div className="muted" style={{ padding: 8, fontSize: 11 }}>Preview</div>
    </div>
  );
}

export function PageSourcePickerModal(props: {
  open: boolean;
  title?: string;
  onClose: () => void;
  sources: PageBindableSourceRow[];
  initialSource?: PageWidgetSource;
  onConfirm: (source: PageWidgetSource) => void;
}) {
  const { open, title = "Choose data source", onClose, sources, initialSource, onConfirm } = props;
  const [opQuery, setOpQuery] = useState("");
  const [selectedOpId, setSelectedOpId] = useState<string | null>(null);
  const [resolvedByKey, setResolvedByKey] = useState<Record<string, ResolveRow>>({});
  const [resolveLoading, setResolveLoading] = useState(false);
  const applyInitialOnOpenRef = useRef(false);

  const filteredSources = useMemo(() => {
    const q = opQuery.trim().toLowerCase();
    if (!q) return sources;
    return sources.filter((s) => s.operation_name.toLowerCase().includes(q) || s.operation_id.toLowerCase().includes(q));
  }, [sources, opQuery]);

  const selectedOp = useMemo(
    () => (selectedOpId ? sources.find((s) => s.operation_id === selectedOpId) ?? null : null),
    [sources, selectedOpId]
  );

  useEffect(() => {
    if (!open) {
      setSelectedOpId(null);
      setResolvedByKey({});
      setOpQuery("");
      return;
    }
    applyInitialOnOpenRef.current = true;
    setOpQuery("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setSelectedOpId((prev) => {
      if (applyInitialOnOpenRef.current) {
        if (initialSource?.operation_id) {
          const id = initialSource.operation_id;
          if (filteredSources.some((s) => s.operation_id === id)) {
            applyInitialOnOpenRef.current = false;
            return id;
          }
          if (filteredSources.length === 0) {
            return prev;
          }
          applyInitialOnOpenRef.current = false;
        } else {
          applyInitialOnOpenRef.current = false;
        }
      }
      if (prev && filteredSources.some((s) => s.operation_id === prev)) return prev;
      return filteredSources[0]?.operation_id ?? null;
    });
  }, [open, filteredSources, initialSource?.operation_id]);

  const loadResolve = useCallback(async (op: PageBindableSourceRow) => {
    const bodySources = op.targets.map((t) => targetToSource(op.operation_id, t));
    if (bodySources.length === 0) {
      setResolvedByKey({});
      return;
    }
    setResolveLoading(true);
    try {
      const data = await apiFetch<{ items: ResolveRow[]; errors: Array<{ source_key: string }> }>(
        "/api/tenant/visualizations/batch-resolve",
        { method: "POST", body: { sources: bodySources } }
      );
      const m: Record<string, ResolveRow> = {};
      for (const item of data.items) m[item.source_key] = item;
      setResolvedByKey(m);
    } catch {
      setResolvedByKey({});
    } finally {
      setResolveLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !selectedOp) return;
    void loadResolve(selectedOp);
  }, [open, selectedOp, loadResolve]);

  if (!open) return null;

  function pickTarget(op: PageBindableSourceRow, t: PageBindableTarget) {
    onConfirm(targetToSource(op.operation_id, t));
    // Do not call onClose() here: parent closes the picker in onConfirm and must keep
    // pending tab/placement state for the layout modal. Overlay dismiss still uses onClose.
  }

  return (
    <div className="modalOverlay" role="presentation" onClick={onClose}>
      <div
        className="modal pageSourcePickerModal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pageSourcePickerTitle"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pageSourcePickerHeader">
          <h2 id="pageSourcePickerTitle" className="pageSourcePickerTitle">
            {title}
          </h2>
          <button className="widgetSettingsClose" type="button" onClick={onClose} aria-label="Close">
            &times;
          </button>
        </div>
        <div className="pageSourcePickerBody">
          <div className="pageSourcePickerTopBar">
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label" htmlFor="pageSourcePickerSearch">
                Search operations
              </label>
              <input
                id="pageSourcePickerSearch"
                className="input pageSourcePickerSearch"
                type="search"
                placeholder="Filter by name or id…"
                value={opQuery}
                onChange={(e) => setOpQuery(e.target.value)}
                aria-label="Filter operations"
              />
            </div>
            <div className="field" style={{ marginBottom: 0 }}>
              <label className="label" htmlFor="pageSourcePickerOp">
                Operation
              </label>
              <select
                id="pageSourcePickerOp"
                className="input pageSourcePickerOpSelect"
                value={selectedOpId ?? ""}
                onChange={(e) => setSelectedOpId(e.target.value || null)}
                disabled={filteredSources.length === 0}
                aria-label="Select operation"
              >
                {filteredSources.length === 0 ? (
                  <option value="">No operations match</option>
                ) : (
                  filteredSources.map((s) => (
                    <option key={s.operation_id} value={s.operation_id}>
                      {s.operation_name}
                      {s.operation_status === "validate" ? " (needs validation)" : ""}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
          {/* Bottom: full-width 3-col preview grid */}
          <div className="pageSourcePickerMain">
            {!selectedOp ? (
              <div className="muted" style={{ padding: 40, textAlign: "center", fontSize: 14 }}>
                Choose an operation to see visualization previews.
              </div>
            ) : (
              <>
                {selectedOp.operation_status === "validate" ? (
                  <div className="pageSourcePickerWarn">This operation requires validation before data can be refreshed.</div>
                ) : null}
                {resolveLoading ? <div className="muted" style={{ fontSize: 13 }}>Loading previews…</div> : null}
                <div className="pageSourcePickerTileGrid">
                  {selectedOp.targets.map((t) => {
                    const src = targetToSource(selectedOp.operation_id, t);
                    const key = widgetSourceKey(src);
                    const resolved = resolvedByKey[key];
                    const rm = resolved?.last?.renderModel;
                    const isInitial =
                      initialSource &&
                      initialSource.operation_id === selectedOp.operation_id &&
                      initialSource.node_id === t.node_id &&
                      (initialSource.port_id?.trim() || "tabular_out") === t.port_id;
                    return (
                      <button
                        key={`${t.node_id}:${t.port_id}`}
                        type="button"
                        className={`pageSourcePickerTile${isInitial ? " isInitial" : ""}`}
                        onClick={() => pickTarget(selectedOp, t)}
                      >
                        <SourceTilePreview portId={t.port_id} renderModel={rm} hasCache={t.has_cache} />
                        <div className="pageSourcePickerTileMeta">
                          <div className="pageSourcePickerTileTitle">{t.name || t.node_id}</div>
                          <div className="pageSourcePickerTilePort">
                            {nodeTypeLabel(t.node_type)} · {t.port_label}
                          </div>
                          {t.last_run_at ? (
                            <div className="pageSourcePickerTileTime muted">Run: {new Date(t.last_run_at).toLocaleString()}</div>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
